import { useSuspenseQuery } from '@apollo/client';
import { Suspense } from 'react';
import type React from 'react';
import { useIntl, FormattedMessage, FormattedPlural } from 'react-intl';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import {
  useParams,
  useLocation,
  useNavigate,
  Redirect,
} from '@xing-com/crate-core-router';
import { PageLayout } from '@xing-com/crate-people-directory-page-layout';
import { Pagination } from '@xing-com/crate-people-directory-pagination';
import { UserChunk } from '@xing-com/crate-people-directory-user-chunk';
import {
  UserMessage,
  UserMessageType,
} from '@xing-com/crate-people-directory-user-message';
import { useMediaListener, BREAKPOINTS } from '@xing-com/util';

import {
  USER_BAG_FILTERS_ID,
  SEO_URL_META_DATA,
  userBagUsernameRoutes,
} from './constants';
import { PeopleUserBagPageDocument } from './graphql/user-bag-page-by-username.query.gql-types';
import { sanitizeNames } from './helper';
import {
  StyledHeadline,
  ContentPlaceholder,
  StyledFiltersForm,
} from './user-bag-page-by-username.styles';

const BASE_URL = `/people/`;

const filterByParams = (
  searchParams: { city_query?: string; jobtitle_query?: string },
  slugOrLetter: string
): string => {
  if (searchParams.city_query && searchParams.city_query !== '') {
    if (searchParams.jobtitle_query && searchParams.jobtitle_query !== '') {
      return `${BASE_URL}${slugOrLetter}?city_query=${searchParams.city_query}&jobtitle_query=${searchParams.jobtitle_query}`;
    }
    return `${BASE_URL}${slugOrLetter}?city_query=${searchParams.city_query}`;
  }
  return `${BASE_URL}${slugOrLetter}/?jobtitle_query=${searchParams.jobtitle_query || ''}`;
};

// Slug can be "bauer-michael" or "bauer-michael-p2"
const SLUG_AND_PAGE_REGEX =
  /((?<slugAndPageSlug>[a-zA-Z]+-[a-zA-Z]+)-p(?<pageFromUrl>[0-9]+))|((?<singleSlug>[a-zA-Z]+-[a-zA-Z]+))/;

type BasicPageLayoutProps = {
  index: boolean;
  pageName: string;
};

const UserBagPageByUsernameInner: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  const { slugOrLetter } = useParams();
  const { slugAndPageSlug, pageFromUrl, singleSlug } =
    slugOrLetter.match(SLUG_AND_PAGE_REGEX).groups;
  const slug = slugAndPageSlug || singleSlug || '';
  const pageFromSlug = +pageFromUrl || 1;

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const breakpoint = useMediaListener();
  const isDesktop = breakpoint === BREAKPOINTS.desktop;

  const currentFilters = {
    city: searchParams.get('city_query') || '',
    jobTitle: searchParams.get('jobtitle_query') || '',
  };

  const { data, error } = useSuspenseQuery(PeopleUserBagPageDocument, {
    variables: {
      slug,
      page: pageFromSlug,
      cityQuery: currentFilters.city,
      jobtitleQuery: currentFilters.jobTitle,
    },
    skip: !slug,
  });
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const onFiltersFormSubmit = (filters: Record<string, string>): void => {
    const searchParams = {
      ...(filters.city && { city_query: filters.city }),
      ...(filters.jobTitle && { jobtitle_query: filters.jobTitle }),
    };
    navigate(filterByParams(searchParams, slugOrLetter));
  };

  const { names, name, totalPages, page, count, breadcrumbs } =
    data?.peopleUserBagPage || {};

  const sanitizedBreadcrumbs = breadcrumbs?.map((breadcrumb) => {
    return {
      title: breadcrumb?.title || '',
      url: `${breadcrumb?.url}` || '',
    };
  });

  const letter = slug[0].toLowerCase();

  if (error || !data?.peopleUserBagPage) {
    return <Redirect to={`/people/${letter}`} />;
  }

  const trackingContextData =
    currentFilters.city || currentFilters.jobTitle
      ? {
          PropSearchCity: currentFilters.city,
          PropSearchTerm: currentFilters.jobTitle,
        }
      : {};

  const seoTitle = formatMessage(
    { id: 'PD_PAGETITLE_USER_BAG' },
    { display_name: name }
  );

  const seoDescription = formatMessage(
    { id: 'PD_META_DESCRIPTION_USER_BAG' },
    { display_name: name, total_count: count, phone_icon: '☎' }
  );

  const fullPageLayoutProps = {
    ...basicPageLayoutProps,
    seoTitle,
    seoDescription,
    trackingContextData,
    index: true,
    seoUrl: `${SEO_URL_META_DATA}${slug}${page === 1 ? '' : `p-${page}`}`,
    pageName: `${letter}/surnames`,
    hidePageHeader: true,
    breadcrumbs: sanitizedBreadcrumbs,
  };

  const filtersFormParams = [
    {
      id: USER_BAG_FILTERS_ID.city,
      labelKey: 'PD_USER_BAG_SEARCH_CITY_LABEL',
      helperTextKey: 'PD_USER_BAG_SEARCH_CITY_INPUT_PLACEHOLDER',
      value: currentFilters.city,
    },
    {
      id: USER_BAG_FILTERS_ID.jobTitle,
      labelKey: 'PD_USER_BAG_SEARCH_OCCUPATION_LABEL',
      helperTextKey: 'PD_USER_BAG_SEARCH_OCCUPATION_INPUT_PLACEHOLDER',
      value: currentFilters.jobTitle,
    },
  ];

  const filtersForm = (
    <StyledFiltersForm
      filters={filtersFormParams}
      onSubmit={onFiltersFormSubmit}
      submitLabelKey="PD_USER_BAG_SEARCH_SUBMIT_BUTTON"
    />
  );

  const headlineValues = { count, display_name: name };
  const headlineMessage = (
    <FormattedPlural
      value={count || 0}
      zero={
        <FormattedMessage
          id="PD_USER_BAG_HEADER_SUBHEADLINES.zero"
          values={headlineValues}
        />
      }
      one={
        <FormattedMessage
          id="PD_USER_BAG_HEADER_SUBHEADLINES.one"
          values={headlineValues}
        />
      }
      other={
        <FormattedMessage
          id="PD_USER_BAG_HEADER_SUBHEADLINES.other"
          values={headlineValues}
        />
      }
    />
  );

  if (!data?.peopleUserBagPage || !names || !names.length) {
    if (page && page > 1) {
      return <Redirect to={`/people/${slug}`} />;
    } else {
      return (
        <PageLayout {...fullPageLayoutProps}>
          {filtersForm}
          <UserMessage type={UserMessageType.emptyUserBags} />
        </PageLayout>
      );
    }
  }

  return (
    <PageLayout {...fullPageLayoutProps}>
      <StyledHeadline
        size={isDesktop ? 'xxlarge' : 'large'}
        forwardedAs={isDesktop ? 'h1' : undefined}
      >
        {headlineMessage}
      </StyledHeadline>
      {filtersForm}
      {names ? (
        <UserChunk names={sanitizeNames(names)} fullWidthLayout />
      ) : null}
      {slug && page && totalPages && totalPages > 1 ? (
        <Pagination
          currentPage={page}
          pagesNumber={totalPages}
          route={userBagUsernameRoutes.USER_BAG_PAGE_PAGINATED}
          routeFirstPage={userBagUsernameRoutes.USER_BAG_PAGE}
          routeParams={{ slug }}
          filterQuery={{
            ...(currentFilters.city && { city: currentFilters.city }),
            ...(currentFilters.jobTitle && {
              jobTitle: currentFilters.jobTitle,
            }),
          }}
        />
      ) : null}
    </PageLayout>
  );
};

const LoadingStateFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  const breakpoint = useMediaListener();
  const isDesktop = breakpoint === BREAKPOINTS.desktop;

  return (
    <PageLayout {...basicPageLayoutProps}>
      <ContentPlaceholder>
        <StyledHeadline size={isDesktop ? 'xxlarge' : 'large'}>
          ...
        </StyledHeadline>
      </ContentPlaceholder>
    </PageLayout>
  );
};

export const UserBagPageByUsername: React.FC = () => {
  const { slugOrLetter } = useParams();
  const { slugAndPageSlug, singleSlug } =
    slugOrLetter.match(SLUG_AND_PAGE_REGEX).groups;
  const slug = slugAndPageSlug || singleSlug || '';
  const letter = slug[0].toLowerCase();

  const basicPageLayoutProps = {
    index: true,
    pageName: `${letter}/surnames`,
    letter,
    hidePageHeader: true,
  };

  return (
    <ErrorBoundary onError={() => <Redirect to={`/people/${letter}`} />}>
      <Suspense fallback={<LoadingStateFallback {...basicPageLayoutProps} />}>
        <UserBagPageByUsernameInner {...basicPageLayoutProps} />
      </Suspense>
    </ErrorBoundary>
  );
};
